import { useEffect, useState } from "react";
import api, { fmtMXN, fmtDate } from "../lib/api";
import Topbar from "../components/Topbar";
import KpiCard from "../components/KpiCard";
import StatusBadge from "../components/StatusBadge";
import PizzaSpinner from "../components/PizzaSpinner";
import { ShoppingCart, FileCheck, Wallet, Users, ArrowUpRight, AlertCircle, AlertTriangle, Copy, CalendarClock } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function CorporateDashboard() {
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [secSummary, setSecSummary] = useState(null);
  const [cxp, setCxp] = useState(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    api.get("/dashboard/corporate").then((r) => setData(r.data)).catch(() => {});
    // Fix WH16 (2026-08-31): panel de cuentas por pagar. Si el endpoint falla
    // el dashboard sigue funcionando igual que antes (el panel no se dibuja).
    api.get("/dashboard/cuentas-por-pagar").then((r) => setCxp(r.data)).catch(() => {});
    api.get("/alerts").then((r) => setAlerts(r.data.filter(a => !a.read && (a.severity === "critical" || a.severity === "warning")).slice(0, 3))).catch(() => {});
    api.get("/security/failed-logins/summary").then((r) => setSecSummary(r.data)).catch(() => {});
  }, []);

  if (!data) return <><Topbar title={t("nav.dashboard")} /><PizzaSpinner /></>;

  const k = data.kpis;

  return (
    <>
      <Topbar title={t("dashboard.greetingCorporate")} subtitle={fmtDate(new Date().toISOString()) + " · " + t("dashboard.overviewSubtitle")} />
      <div className="p-6 space-y-6">
        {/* Critical alerts banner */}
        {alerts.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3"
          >
            <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-5 h-5 text-amber-700" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-amber-900 mb-1">{t("dashboard.alertsBanner", { count: alerts.length })}</div>
              <div className="text-sm text-amber-800 space-y-0.5">
                {alerts.slice(0, 2).map((a) => (
                  <div key={a.id}>· {a.message}</div>
                ))}
              </div>
            </div>
            <button
              onClick={() => navigate("/alertas")}
              data-testid="alerts-banner-btn"
              className="text-sm font-semibold text-amber-900 hover:underline whitespace-nowrap"
            >
              {t("common.viewAll")} →
            </button>
          </motion.div>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard testId="kpi-pos-pending" label={t("dashboard.kpis.posPending")} value={k.pos_pending} icon={ShoppingCart} accent={k.pos_pending > 0} />
          <KpiCard testId="kpi-invoices-pending" label={t("dashboard.kpis.invoicesPending")} value={k.invoices_to_validate} icon={FileCheck} />
          <KpiCard testId="kpi-spend-month" label={t("dashboard.kpis.spendMonth")} value={fmtMXN(k.spend_this_month)} icon={Wallet} />
          <KpiCard testId="kpi-suppliers-active" label={t("dashboard.kpis.suppliersActive")} value={k.suppliers_active} icon={Users} />
        </div>

        {/* Fix WH16 (2026-08-31): cuentas por pagar. Pedido explicito de Claudia
            (video 25/08, min 3:13 y 8:50): vencidas, por pagar esta semana,
            discrepancias, duplicadas y pagadas de mas, mas el consolidado por
            sucursal. Todo sale del endpoint /dashboard/cuentas-por-pagar. */}
        {cxp && (
          <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4" data-testid="cxp-panel">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="font-display font-bold text-base text-[#1A1A1A]">Cuentas por pagar</div>
              <button onClick={() => navigate("/facturas")} data-testid="cxp-ver-facturas"
                className="text-sm font-semibold text-slate-600 hover:underline">Ver facturas →</button>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <button onClick={() => navigate("/facturas")} data-testid="cxp-vencidas"
                className={`text-left rounded-xl p-3 border transition-colors ${cxp.kpis.vencidas_count > 0 ? "bg-red-50 border-red-200 hover:bg-red-100" : "bg-slate-50 border-slate-200"}`}>
                <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  <AlertTriangle className="w-3.5 h-3.5" /> Vencidas
                </div>
                <div className={`text-lg font-bold font-mono mt-1 ${cxp.kpis.vencidas_count > 0 ? "text-red-700" : "text-slate-700"}`}>{fmtMXN(cxp.kpis.vencidas_monto)}</div>
                <div className="text-xs text-slate-500">{cxp.kpis.vencidas_count} facturas</div>
              </button>
              <button onClick={() => navigate("/facturas")} data-testid="cxp-semana"
                className="text-left rounded-xl p-3 border bg-amber-50 border-amber-200 hover:bg-amber-100 transition-colors">
                <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  <CalendarClock className="w-3.5 h-3.5" /> Por pagar esta semana
                </div>
                <div className="text-lg font-bold font-mono mt-1 text-amber-800">{fmtMXN(cxp.kpis.esta_semana_monto)}</div>
                <div className="text-xs text-slate-500">{cxp.kpis.esta_semana_count} facturas</div>
              </button>
              <div className="rounded-xl p-3 border bg-slate-50 border-slate-200">
                <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  <CalendarClock className="w-3.5 h-3.5" /> Por vencer (30 días)
                </div>
                <div className="text-lg font-bold font-mono mt-1 text-slate-700">{fmtMXN(cxp.kpis.por_vencer_30_monto)}</div>
                <div className="text-xs text-slate-500">{cxp.kpis.por_vencer_30_count} facturas</div>
              </div>
              <div className="rounded-xl p-3 border bg-slate-50 border-slate-200">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Total por pagar</div>
                <div className="text-lg font-bold font-mono mt-1 text-slate-900">{fmtMXN(cxp.kpis.total_por_pagar)}</div>
                <div className="text-xs text-slate-500">{cxp.kpis.sin_fecha_count > 0 ? `${cxp.kpis.sin_fecha_count} sin fecha de vencimiento` : "Todas con vencimiento"}</div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className={`rounded-xl p-3 border ${cxp.kpis.discrepancia_count > 0 ? "bg-red-50 border-red-200" : "bg-slate-50 border-slate-200"}`} data-testid="cxp-discrepancia">
                <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500"><AlertTriangle className="w-3.5 h-3.5" /> Con discrepancia</div>
                <div className="text-lg font-bold mt-1">{cxp.kpis.discrepancia_count}</div>
              </div>
              <div className={`rounded-xl p-3 border ${cxp.kpis.duplicadas_count > 0 ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-200"}`} data-testid="cxp-duplicadas">
                <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500"><Copy className="w-3.5 h-3.5" /> Posibles duplicadas</div>
                <div className="text-lg font-bold mt-1">{cxp.kpis.duplicadas_count}</div>
              </div>
              <div className={`rounded-xl p-3 border ${cxp.kpis.pagadas_de_mas_count > 0 ? "bg-red-50 border-red-200" : "bg-slate-50 border-slate-200"}`} data-testid="cxp-pagadas-de-mas">
                <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500"><AlertTriangle className="w-3.5 h-3.5" /> Pagadas de más</div>
                <div className="text-lg font-bold mt-1">{cxp.kpis.pagadas_de_mas_count}</div>
              </div>
            </div>

            {cxp.por_sucursal?.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="cxp-por-sucursal">
                  <thead>
                    <tr className="border-b border-slate-200 text-left">
                      <th className="py-2 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Sucursal</th>
                      <th className="py-2 text-[11px] font-bold text-slate-500 uppercase tracking-wider text-right">Facturas</th>
                      <th className="py-2 text-[11px] font-bold text-slate-500 uppercase tracking-wider text-right">Por pagar</th>
                      <th className="py-2 text-[11px] font-bold text-slate-500 uppercase tracking-wider text-right">Vencido</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cxp.por_sucursal.map((b) => (
                      <tr key={b.branch_id} className="border-b border-slate-100">
                        <td className="py-2 font-semibold">{b.branch_name}</td>
                        <td className="py-2 text-right font-mono">{b.count}</td>
                        <td className="py-2 text-right font-mono">{fmtMXN(b.total)}</td>
                        <td className={`py-2 text-right font-mono font-bold ${b.vencido > 0 ? "text-red-700" : "text-slate-400"}`}>{fmtMXN(b.vencido)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Security widget: intentos fallidos 24h */}
        {secSummary && (
          <button
            data-testid="security-failed-logins-widget"
            onClick={() => navigate("/perfil/seguridad")}
            className={`w-full text-left rounded-2xl border p-4 flex items-center gap-4 transition-colors ${
              secSummary.failed_24h >= 50
                ? "bg-red-50 border-red-200 hover:bg-red-100"
                : "bg-white border-slate-200 hover:bg-slate-50"
            }`}
          >
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${secSummary.failed_24h >= 50 ? "bg-red-100" : "bg-slate-100"}`}>
              <AlertCircle className={`w-5 h-5 ${secSummary.failed_24h >= 50 ? "text-red-600" : "text-slate-500"}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className={`font-bold ${secSummary.failed_24h >= 50 ? "text-red-900" : "text-[#1A1A1A]"}`}>
                {t("security.widget.failed24h", { count: secSummary.failed_24h })}
              </div>
              <div className="text-sm text-slate-500">
                {t("security.widget.lockedAccounts", { count: secSummary.locked_accounts })}
              </div>
            </div>
            <span className="text-sm font-semibold text-slate-500 whitespace-nowrap">{t("common.viewAll")} →</span>
          </button>
        )}

        {/* Chart + Quick actions */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-display font-bold text-lg text-[#1A1A1A]">{t("dashboard.spendByBranch")}</h2>
                <p className="text-xs text-slate-500">{t("dashboard.spendByBranchSub")}</p>
              </div>
              <button onClick={() => navigate("/reportes")} className="text-xs font-semibold text-slate-500 hover:text-[#1A1A1A] flex items-center gap-1">
                {t("dashboard.seeReports")} <ArrowUpRight className="w-3 h-3" />
              </button>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.spend_by_branch}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="branch" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip
                    contentStyle={{ background: "#1A1A1A", border: "none", borderRadius: 12, color: "white", fontSize: 12 }}
                    cursor={{ fill: "rgba(255, 223, 0, 0.1)" }}
                    formatter={(v) => fmtMXN(v)}
                  />
                  <Bar dataKey="spend" fill="#FFDF00" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-gradient-to-br from-[#1A1A1A] to-[#2D2D2D] text-white rounded-2xl p-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#FFDF00]/10 rounded-full blur-2xl" />
            <h2 className="font-display font-bold text-lg relative">{t("dashboard.quickActions")}</h2>
            <p className="text-xs text-slate-400 relative mt-1">{t("dashboard.quickActionsSub")}</p>
            <div className="mt-5 space-y-2 relative">
              <button onClick={() => navigate("/ocs")} className="w-full text-left px-4 py-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors flex items-center justify-between group">
                <span className="text-sm font-semibold">{t("dashboard.approvePos")}</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-[#FFDF00] text-[#1A1A1A] font-bold">{k.pos_pending}</span>
              </button>
              <button onClick={() => navigate("/facturas")} className="w-full text-left px-4 py-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors flex items-center justify-between">
                <span className="text-sm font-semibold">{t("dashboard.validateInvoices")}</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-white/10">{k.invoices_to_validate}</span>
              </button>
              <button onClick={() => navigate("/proveedores")} className="w-full text-left px-4 py-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors flex items-center justify-between">
                <span className="text-sm font-semibold">{t("dashboard.manageSuppliers")}</span>
                <ArrowUpRight className="w-4 h-4 text-slate-400" />
              </button>
            </div>
          </div>
        </div>

        {/* Recent POs */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="flex items-center justify-between p-6 pb-4">
            <div>
              <h2 className="font-display font-bold text-lg text-[#1A1A1A]">{t("dashboard.latestPos")}</h2>
              <p className="text-xs text-slate-500">{t("dashboard.lastTenMostRecent")}</p>
            </div>
            <button onClick={() => navigate("/ocs")} className="text-xs font-semibold text-slate-500 hover:text-[#1A1A1A]">
              {t("common.viewAll")} →
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50/80 border-y border-slate-200">
                <tr>
                  <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("po.folio")}</th>
                  <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("po.branch")}</th>
                  <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("common.supplier")}</th>
                  <th className="text-right px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("common.total")}</th>
                  <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("common.status")}</th>
                  <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("common.date")}</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_pos.map((po, idx) => (
                  <motion.tr
                    key={po.id}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.03 }}
                    onClick={() => navigate(`/ocs/${po.id}`)}
                    className="border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer transition-colors"
                    data-testid={`recent-po-row-${po.folio}`}
                  >
                    <td className="px-6 py-3.5 font-mono text-sm font-semibold text-[#1A1A1A]">{po.folio}</td>
                    <td className="px-6 py-3.5 text-sm">{po.branch_name}</td>
                    <td className="px-6 py-3.5 text-sm text-slate-700">{po.supplier_name}</td>
                    <td className="px-6 py-3.5 text-sm font-semibold text-right text-[#1A1A1A]">{fmtMXN(po.total)}</td>
                    <td className="px-6 py-3.5"><StatusBadge status={po.status} /></td>
                    <td className="px-6 py-3.5 text-sm text-slate-500">{fmtDate(po.created_at)}</td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
